import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { pushAnalyticsEvent } from "./analytics.js";
import { appendSectionHeadlineReveal } from "./section-headline-gsap.js";
import { appendMapContentReveal } from "./map-section-gsap.js";

gsap.registerPlugin(ScrollTrigger);

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

const mapSection = document.querySelector(".map-section");

/**
 * @param {HTMLElement} contentEl — `.map-section__content`
 * @param {string} countryId — matches SVG `#id` and `#map-source-{id}`
 */
function copyCountryIntoSurface(contentEl, countryId) {
  const source = document.getElementById(`map-source-${countryId}`);
  const copyEl = contentEl.querySelector("#map-section-copy");
  const previewsHost = contentEl.querySelector("#map-section-previews-host");
  const metaEl = contentEl.querySelector("#map-section-meta");
  if (!source || !copyEl || !previewsHost || !metaEl) {
    return false;
  }

  const srcPreviews = source.querySelector(".map-section__previews");
  previewsHost.replaceChildren();
  if (srcPreviews) {
    previewsHost.appendChild(srcPreviews.cloneNode(true));
  }

  const rest = document.createElement("div");
  rest.innerHTML = source.innerHTML;
  rest.querySelector(".map-section__previews")?.remove();

  const copyBlock = rest.querySelector(".map-section__copy-block");
  const metaBlock = rest.querySelector(".map-section__meta-block");

  copyEl.innerHTML = copyBlock ? copyBlock.innerHTML : "";
  metaEl.innerHTML = metaBlock ? metaBlock.innerHTML : "";
  return true;
}

/**
 * @param {string} activeId
 */
function setActiveCountry(activeId) {
  document.querySelectorAll(".map__country").forEach((node) => {
    node.classList.toggle("is-active", node.id === activeId);
  });
}

/** Matches `#map-section-copy { scroll-margin-top }` if CSS not loaded yet. */
const MAP_COPY_SCROLL_MARGIN_FALLBACK_PX = 80;

function getScrollMarginTopPx(el) {
  const raw = getComputedStyle(el).scrollMarginTop;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : MAP_COPY_SCROLL_MARGIN_FALLBACK_PX;
}

/**
 * Scroll the window so the updated country copy (`#map-section-copy`) aligns
 * under the top edge, honoring `scroll-margin-top` (fixed header clearance).
 *
 * Uses explicit `scrollTo` — not `scrollIntoView` on `.map-section__wrapper`:
 * the wrapper is taller than the viewport, so the scrollport can sit fully
 * inside its bounds and browsers skip scrolling.
 */
function scrollWindowToMapCopy() {
  const copyEl = document.getElementById("map-section-copy");
  if (!copyEl || !mapSection?.contains(copyEl)) {
    return;
  }

  const margin = getScrollMarginTopPx(copyEl);
  const rect = copyEl.getBoundingClientRect();
  const y =
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;
  const targetY = Math.max(0, y + rect.top - margin);

  window.scrollTo({
    top: targetY,
    left: 0,
    behavior: reducedMotion ? "auto" : "smooth",
  });

  window.setTimeout(
    () => {
      ScrollTrigger.refresh();
    },
    reducedMotion ? 50 : 520,
  );
}

/** Wait for layout after DOM updates / GSAP so `getBoundingClientRect` is stable. */
function scheduleScrollToMapCopy() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scrollWindowToMapCopy();
    });
  });
}

if (mapSection) {
  const headline = mapSection.querySelector(
    ".map-section__inner .page-section__headline",
  );
  const mapImageWrapper = mapSection.querySelector(
    ".map-section__image-wrapper",
  );
  const content = mapSection.querySelector(".map-section__content");

  if (!reducedMotion) {
    gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: mapSection,
          start: "top 85%",
          toggleActions: "play none none none",
        },
        defaults: { ease: "power2.out" },
      });

      appendSectionHeadlineReveal(tl, headline, 0, { withRule: true });

      const mapAndContentStart = 0.32;

      if (mapImageWrapper) {
        tl.fromTo(
          mapImageWrapper,
          { autoAlpha: 0, x: 56 },
          {
            autoAlpha: 1,
            x: 0,
            duration: 1.05,
            ease: "power2.out",
            force3D: true,
          },
          mapAndContentStart,
        );
      }

      if (content) {
        appendMapContentReveal(tl, content, mapAndContentStart, {
          variant: "intro",
        });
      }
    }, mapSection);
  }

  const countryNodes = mapSection.querySelectorAll(".map__country");
  countryNodes.forEach((node) => {
    node.addEventListener("click", (e) => {
      const id = e.currentTarget.id;
      if (!id || !content) {
        return;
      }
      if (!document.getElementById(`map-source-${id}`)) {
        return;
      }

      const country = e.currentTarget.getAttribute("data-country-name")?.trim() || "";
      if (country) {
        pushAnalyticsEvent("select_country", {
          entry_point: "homepage_map",
          country,
        });
      }

      const copyRoot = content.querySelector("#map-section-copy");
      const metaRoot = content.querySelector("#map-section-meta");
      const subheadline = copyRoot?.querySelector(".map-section__subheadline");
      const lead = copyRoot?.querySelector(".map-section__lead");
      const statsRow = metaRoot?.querySelector(".map-section__stats");
      const cta = metaRoot?.querySelector(".map-section__cta");
      const statValues = metaRoot?.querySelectorAll(".map-section__stat-value");
      const previewImgs = content.querySelectorAll(
        ".map-section__previews .map-section__preview",
      );
      const isReselect = content.classList.contains(
        "map-section__content--has-country",
      );

      const runCountrySwitch = () => {
        if (!copyCountryIntoSurface(content, id)) {
          return;
        }
        content.classList.add("map-section__content--has-country");
        setActiveCountry(id);

        if (reducedMotion) {
          scheduleScrollToMapCopy();
          return;
        }

        const tl = gsap.timeline({ defaults: { ease: "none" } });
        appendMapContentReveal(tl, content, 0, { variant: "switch" });
        scheduleScrollToMapCopy();
      };

      const continueCountryClick = () => {
        gsap.killTweensOf(
          [
            subheadline,
            lead,
            statsRow,
            ...(statValues ? Array.from(statValues) : []),
            ...(previewImgs ? Array.from(previewImgs) : []),
          ].filter(Boolean),
        );

        if (isReselect && cta && !reducedMotion) {
          gsap.killTweensOf(cta);
          gsap.to(cta, {
            autoAlpha: 0,
            duration: 0.22,
            ease: "none",
            overwrite: true,
            onComplete: runCountrySwitch,
          });
          return;
        }

        if (cta) {
          gsap.killTweensOf(cta);
        }

        runCountrySwitch();
      };

      continueCountryClick();
    });

    node.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") {
        return;
      }
      e.preventDefault();
      node.click();
    });

    if (!node.hasAttribute("tabindex")) {
      node.setAttribute("tabindex", "0");
    }
    if (!node.hasAttribute("role")) {
      node.setAttribute("role", "button");
    }
  });
}
